import { Link } from 'react-router-dom';
import { useResource } from '../lib/useResource';
import { api, type AttentionResponse } from '../lib/api';
import { Card, CabecalhoDePainel, TituloDePainel, LinkDePainel, Barra } from '../components/Painel';
import { DataValue } from '../components/DataValue';
import { dataStateFromKpi, type DataState } from '../lib/dataState';
import {
  OPERATIONAL_STATE_LABEL,
  OPERATIONAL_STATE_EXPLICACAO,
  type OperationalState,
} from '../lib/navigation';
import type { ConfigurationResponse, KpisResponse, OperationsResponse } from '../lib/api';

/**
 * Visao geral, no layout entregue pelo cliente em 17/09/2026.
 *
 * Ela foi REESCRITA porque a primeira aplicacao do layout trocou a casca e os
 * tokens de cor e deixou esta tela como era: titulo, tabela crua e caixas
 * pequenas. O dono comparou com o PNG de referencia e a diferenca era obvia.
 * A casca nao e o layout; esta tela e a que o PNG desenha.
 *
 * ## O que veio do desenho, e o que NAO veio
 *
 * Veio a ESTRUTURA: saudacao, o card de sistema em destaque ao lado da fileira
 * de metricas, o cartao de integracoes com um bloco por provedor, e a faixa de
 * paineis embaixo.
 *
 * NAO veio o CONTEUDO dos numeros. O PNG mostra "5 / 7 posts", "23 leads",
 * "4,8%" e "8.422 registros", e o proprio handoff do pacote proibe isso:
 * "nao inventar numeros". Aqueles valores sao ilustracao. Cada card aqui mostra
 * o ESTADO real do indicador (`amostra insuficiente`, `indisponivel`,
 * `nao configurado`), que e a informacao honesta enquanto nenhuma fonte de
 * provedor esta conectada.
 *
 * Copiar os numeros do desenho teria produzido exatamente a tela que o dono
 * queria ver, e ela estaria mentindo.
 *
 * ## A ordem, onde o desenho e a SPEC discordam
 *
 * Back Office §7 e Wireframes §3 exigem a atencao do dono ANTES de qualquer
 * grafico. O PNG poe "Proximas Acoes" no meio da pagina, abaixo dos numeros. O
 * README do pacote resolve: "em caso de conflito visual entre o mockup e uma
 * regra da SPEC, a SPEC prevalece". Entao a atencao abre a tela.
 *
 * ## O que o desenho nao tem e a SPEC exige
 *
 * O funil ponta-a-ponta do §7.3. Ele nao aparece no PNG, e a primeira versao
 * desta reescrita o deixou cair junto — encolher a tela para caber no desenho
 * teria sido trocar uma exigencia da spec por estetica.
 */

/** Os north-star da spec §7.2. Maximo de seis acima da dobra. */
const NORTH_STAR_IDS = ['KPI-001', 'KPI-002', 'KPI-003', 'KPI-004', 'KPI-060', 'KPI-061'] as const;

/** Etapas do funil ponta-a-ponta (§7.3). Etapa indisponivel fica NULA e visivel. */
const FUNNEL_STAGES = [
  'oportunidades',
  'selecionados',
  'aprovados',
  'produzidos',
  'publicados',
  'alcance qualificado',
  'seguidores qualificados',
  'intenção comercial',
  'lead qualificado',
  'reunião',
  'pipeline/receita',
] as const;

const EFFECT_LABEL: Record<string, string> = {
  publication: 'Publicação no Instagram',
  paid_media: 'Mídia paga',
  digital_twin: 'Render do Digital Twin',
  community_auto_send: 'Resposta automática na comunidade',
  sales_auto_send: 'Mensagem comercial automática',
};

/** Sigla do bloco de integracao, como o desenho desenha (IG, M, YT, GD). */
const PROVIDER_SIGLA: Record<string, string> = {
  instagram: 'IG',
  meta: 'M',
  meta_ads: 'M',
  youtube: 'YT',
  google_drive: 'GD',
  google_calendar: '31',
  heygen: 'HG',
  resend: '@',
};

/**
 * O painel de publicacao do desenho, ligado a configuracao REAL.
 *
 * O PNG mostra 3 / 4 / 7 / 10. Esses numeros existem como parametro de
 * configuracao, entao o painel LE o valor aprovado em vez de repetir o desenho:
 * no dia em que o dono mudar o teto, a tela acompanha. Sem versao aprovada, o
 * parametro diz "nao configurado" em vez de mostrar o numero do rascunho.
 */
const PUBLICACAO_LINHAS = [
  { chave: 'publishing.operational_floor_posts_per_day', rotulo: 'Piso operacional' },
  { chave: 'publishing.desired_min_posts_per_day', rotulo: 'Mínimo desejado' },
  { chave: 'publishing.desired_max_posts_per_day', rotulo: 'Máximo desejado' },
  { chave: 'publishing.absolute_auto_max_posts_per_day', rotulo: 'Máximo automático' },
] as const;

export function Overview({
  operations,
  kpis,
  configuration,
  actorName,
}: {
  operations: OperationsResponse;
  kpis: KpisResponse;
  configuration: ConfigurationResponse;
  actorName: string;
}) {
  const byId = new Map(kpis.kpis.map((k) => [k.kpi_id, k]));
  const porChave = new Map(configuration.parameters.map((p) => [p.parameter_key, p]));
  const switches = operations.external_effect_switches;
  const ligados = Object.entries(switches).filter(([, on]) => on);
  const pausado = operations.system_state === 'paused';
  const parcial = operations.system_state === 'partially_paused';
  /*
    O que a tela AFIRMA vem do servidor. Ate 17/09 ela derivava "em operacao"
    de `system_state === 'running'`, que significa apenas "ninguem apertou
    pausa", e escrevia isso num ambiente onde nada publica, gasta ou envia.
  */
  const estado: OperationalState = operations.operational_state;
  const operando = estado === 'running';
  /* Sem nada aberto, o painel de atencao e uma linha, e nao um bloco. */
  /*
    A caixa de atencao e BUSCADA, e nao deduzida.

    Ate aqui esta tela escrevia "Nenhum item de atencao aberto" sempre que nao
    havia pausa, sem nunca perguntar ao servidor. Ela nao tinha como saber: a
    caixa e derivada de dez condicoes (motor, retencao, filtro de e-mail,
    escalacao, hold, pausa), e nenhuma delas e pausa de sistema.

    Afirmar "nenhum item" sem medir e o mesmo defeito de dizer "em operacao"
    por nao haver pausa: uma frase tranquilizadora derivada da coisa errada.
  */
  const atencao = useResource<AttentionResponse>(() => api.attention(), []);

  /*
    Primeiro nome, para a saudacao ficar como no desenho ("Olá, Sergio."). Se o
    servidor devolver um identificador sem espaco, a saudacao usa ele inteiro em
    vez de cortar no lugar errado.
  */
  const primeiroNome = actorName.trim().split(/\s+/)[0] ?? actorName;
  /*
    As capacidades agrupadas por PROVEDOR.

    O desenho mostra um bloco por provedor (Instagram, Meta, YouTube, Drive), e
    nao um por capacidade. A primeira versao listava capacidade a capacidade, e
    com 27 delas a tela virou oito blocos escritos "Instagram" com um sufixo
    tecnico embaixo. So vendo no app com dado real deu para perceber: a fixture
    do teste tinha um provedor por capacidade, que e a forma que esconde
    exatamente este defeito.
  */
  const porProvedor = [
    ...operations.capabilities
      .reduce((mapa, c) => {
        const atual = mapa.get(c.provider) ?? { provider: c.provider, total: 0, disponiveis: 0 };
        atual.total += 1;
        if (c.state === 'available') atual.disponiveis += 1;
        mapa.set(c.provider, atual);
        return mapa;
      }, new Map<string, { provider: string; total: number; disponiveis: number }>())
      .values(),
  ];

  return (
    <div className="space-y-4">
      {/* -------------------------- Saudacao --------------------------- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Olá, {primeiroNome}.</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Este é o estado do Ferrari Instagram AI agora.
          </p>
        </div>
        <span
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
            pausado
              ? 'border-danger-border bg-danger-surface text-danger-ink'
              : parcial
                ? 'border-warn-border bg-warn-surface text-warn-ink'
                : operando
                  ? 'border-ok-border bg-ok-surface text-ok-ink'
                  : 'border-accent-border bg-accent-soft text-accent-ink'
          }`}
          data-testid="selo-estado"
          title={OPERATIONAL_STATE_EXPLICACAO[estado]}
        >
          Sistema {OPERATIONAL_STATE_LABEL[estado]}
        </span>
      </div>

      {/*
        Atencao do dono: ANTES de qualquer numero (Back Office §7, Wireframes §3).

        O desenho poe "Proximas Acoes" no meio da pagina; a SPEC exige a atencao
        antes do primeiro grafico, e o README do pacote resolve o conflito a
        favor da SPEC. O que mudou foi o TAMANHO: sem nada aberto ela e uma
        linha, e nao o bloco de tres paragrafos que empurrava a fileira de
        metricas para fora da primeira tela. Com item aberto ela cresce.
      */}
      <section aria-labelledby="secao-atencao">
        {atencao.kind === 'ready' && atencao.data.visible_to_actor === 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line bg-card px-4 py-2.5 shadow-card">
            <TituloDePainel id="secao-atencao" texto="Atenção do dono" />
            <span className="text-sm text-ink-muted" data-testid="atencao-resumo">
              Nenhum item aberto que você possa resolver.
              {atencao.data.total > atencao.data.visible_to_actor
                ? ` ${atencao.data.total - atencao.data.visible_to_actor} item(ns) existem fora do seu alcance.`
                : ''}
            </span>
            <LinkDePainel para="/atencao" texto="Ver todas" />
          </div>
        ) : (
          <Card
            className={
              atencao.kind === 'ready' && atencao.data.visible_to_actor > 0
                ? 'border-warn-border'
                : ''
            }
          >
            <CabecalhoDePainel
              id="secao-atencao"
              texto="Atenção do dono"
              para="/atencao"
              acao="Ver todas"
            />
            {atencao.kind === 'loading' && (
              <p className="text-sm text-ink-muted" data-testid="atencao-resumo">
                Medindo a caixa de atenção…
              </p>
            )}
            {atencao.kind === 'error' && (
              /*
                Falha de leitura NAO vira "nenhum item": zero por erro e a
                mentira mais perigosa desta tela, porque ela tranquiliza.
              */
              <p className="text-sm text-danger-ink" data-testid="atencao-resumo">
                Não foi possível medir a caixa de atenção. Isso não significa que não há nada:
                significa que esta tela não sabe. {atencao.message}
              </p>
            )}
            {atencao.kind === 'ready' && atencao.data.visible_to_actor > 0 && (
              <>
                <p className="text-sm font-semibold" data-testid="atencao-resumo">
                  {atencao.data.visible_to_actor} item(ns) esperando você.
                </p>
                <ul className="mt-2 space-y-1 text-sm text-ink-muted">
                  {atencao.data.items.slice(0, 4).map((item) => (
                    <li key={`${item.condition_type}:${item.condition_id}`}>{item.summary}</li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        )}
      </section>

      {/*
        A fileira de metricas do desenho: SEIS colunas, com o card de sistema
        em destaque na primeira.

        A primeira versao punha o sistema sozinho ao lado de uma grade 3x2, e a
        tela nao lembrava o mockup. O `.g6` do desenho e
        `repeat(6, minmax(130px,1fr))`; aqui sao SETE celulas (o sistema mais
        os seis north-star), entao a fileira fecha em `grid-cols-7` na tela
        larga. Valor arbitrario com virgula nao gera CSS, e o guard de classes
        pegou isso na hora: contagem de coluna e literal e resolve.
      */}
      <TituloDePainel id="secao-northstar" texto="Resultados north-star" className="mb-3" />
      {/*
        O card de SISTEMA divide a fileira com os north-star, mas nao e um
        deles: ele nao e indicador, e um interruptor. A primeira versao o pos
        dentro da lista de definicao e a contagem virou sete, o que os testes
        pegaram na hora (a spec limita a seis acima da dobra).

        `contents` resolve os dois lados: a secao e a lista deixam de gerar caixa
        propria, entao os cards viram itens diretos da grade e a semantica
        continua inteira.
      */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8">
        {/*
            O card de sistema e o `.masterbox` do mockup: gradiente e borda
            PROPRIOS, verdes quando opera e vermelhos quando para. Com o card
            comum ele ficava cinza no meio da fileira.
          */}
        <div
          className={`rounded-card border p-4 shadow-card ${
            pausado
              ? 'border-master-paused bg-master-paused'
              : operando
                ? 'border-master bg-master'
                : 'border-line bg-card'
          }`}
        >
          <dt className="text-xs text-ink-muted">Sistema</dt>
          <dd
            className={`my-1.5 text-[30px] font-extrabold leading-none ${
              pausado ? 'text-danger' : operando ? 'text-ok' : 'text-accent'
            }`}
            data-testid="estado-sistema-card"
          >
            {/*
              "ON" so quando o sistema pode produzir efeito externo. Antes disto
              ele dizia ON sempre que nao havia pausa, e o ambiente de teste
              aparecia identico a producao rodando.
            */}
            {pausado ? 'PARADO' : operando ? 'ON' : 'EM TESTE'}
          </dd>
          <p className="text-sm text-ink-muted">{OPERATIONAL_STATE_EXPLICACAO[estado]}</p>
          {/*
              O desenho poe o botao de desligar aqui. Ele e um LINK para o
              Master Switch, e nao uma segunda copia do formulario de parada: o
              motivo escrito e obrigatorio e fica no registro do comando.
            */}
          <Link
            to="/master-switch"
            className={`mt-3 block rounded-[9px] border px-3 py-2 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-accent ${
              pausado
                ? 'border-ok-border bg-ok-surface text-ok-ink'
                : 'border-danger-border bg-danger-surface text-danger-ink'
            }`}
          >
            {pausado ? 'Retomar' : operando ? 'Desligar operação' : 'Parar o sistema'}
          </Link>
        </div>

        {/*
          O card de MIDIA PAGA do desenho, ao lado do sistema.

          Ele nao e north-star: e operacional, como o "Midia paga R$ 0 / Teto
          R$ 1.000/mes" que o mockup desenha com barra. O indicador real e o
          KPI-044 (BudgetUtilization), uma razao entre 0 e 1, entao a barra tem
          lastro medido. Sem valor aprovado ele mostra o ESTADO e nao desenha
          barra: barra em zero seria lida como gasto zero, e ausencia de medida
          nao e zero.
        */}
        {(() => {
          const orcamento = byId.get('KPI-044');
          if (orcamento === undefined) return null;
          const estado = dataStateFromKpi(orcamento);
          const razao = typeof orcamento.value === 'number' ? orcamento.value : null;
          return (
            <div
              className="rounded-card border border-line bg-card p-4 shadow-card"
              data-testid="card-orcamento"
            >
              <p className="text-xs text-ink-muted">Mídia paga</p>
              <p className="my-1.5 text-[26px] font-bold leading-tight">
                <DataValue state={estado} label={orcamento.name} />
              </p>
              <p className="text-sm text-warn">Auto-execução desligada</p>
              {razao !== null && (
                <Barra
                  fracao={razao}
                  rotulo="Uso do orçamento mensal de mídia paga"
                  testId="barra-orcamento"
                />
              )}
              <p className="mt-2 line-clamp-2 text-xs text-ink-faint">{orcamento.name} (KPI-044)</p>
            </div>
          );
        })()}

        <section aria-labelledby="secao-northstar" className="contents">
          <dl className="contents">
            {NORTH_STAR_IDS.map((id) => {
              const kpi = byId.get(id);
              if (!kpi) return null;
              const state: DataState = dataStateFromKpi(kpi);
              /*
              A BARRA so aparece quando existe razao MEDIDA.

              O AQI traz cobertura de classificacao e o minimo exigido, que e
              exatamente a barra que o desenho mostra ("Coverage 68%,
              threshold >= 60%"). Onde nao ha razao, nao ha barra: uma barra em
              zero seria lida como resultado zero, e ausencia de medida nao e
              zero.
            */
              const c = kpi.coverage;
              const barra =
                c !== null && c.minimum_classified_coverage !== null
                  ? {
                      fracao:
                        c.classified_coverage / Math.max(c.minimum_classified_coverage, 0.0001),
                      rotulo: `Cobertura de classificação de ${kpi.name}`,
                    }
                  : null;
              return (
                <div
                  key={id}
                  className="rounded-card border border-line bg-card p-4 shadow-card"
                  data-testid={`kpi-${id}`}
                >
                  <dt className="text-xs text-ink-muted">
                    {kpi.name} <span className="text-ink-faint">({id})</span>
                  </dt>
                  {/*
                  O numero grande mostra o ESTADO quando nao ha valor confiavel.
                  O desenho poe "23" e "4,8%" aqui; aqueles sao de ilustracao, e
                  um deles no lugar do estado seria lido como resultado real.
                */}
                  <dd className="my-1.5 text-[26px] font-bold leading-tight">
                    <DataValue state={state} label={kpi.name} />
                  </dd>
                  {c !== null && (
                    <p className="text-sm text-ok">
                      Cobertura {Math.round(c.classified_coverage * 100)}%
                    </p>
                  )}
                  {barra !== null && (
                    <Barra fracao={barra.fracao} rotulo={barra.rotulo} testId={`barra-${id}`} />
                  )}
                  {/*
                  A regra de qualidade em DUAS linhas, com o texto inteiro no
                  `title`. Ela e o que explica o estado, entao nao pode sumir;
                  mas inteira ela deixava cada card de uma altura e a fileira
                  do desenho virava um serrilhado.
                */}
                  <p className="mt-2 line-clamp-2 text-xs text-ink-faint" title={kpi.quality_rule}>
                    {kpi.quality_rule}
                  </p>
                </div>
              );
            })}
          </dl>
        </section>
      </div>

      {/* ----------------------- Integracoes ---------------------------- */}
      <section aria-labelledby="secao-integracoes">
        <Card>
          <CabecalhoDePainel
            id="secao-integracoes"
            texto="Integrações"
            para="/integracoes"
            acao="Ver todas"
          />
          <p className="-mt-2 mb-3 text-xs text-ink-muted">
            {porProvedor.filter((p) => p.disponiveis === p.total).length} de {porProvedor.length}{' '}
            provedores com todas as capacidades disponíveis
          </p>
          {porProvedor.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Nenhuma capacidade registrada ainda. A sondagem preenche esta lista quando rodar.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {porProvedor.map((p) => {
                const tudoOk = p.disponiveis === p.total;
                return (
                  <div
                    key={p.provider}
                    className="flex items-center gap-3 rounded-[11px] border border-line bg-surface-sunken p-3"
                    data-testid={`integracao-${p.provider}`}
                  >
                    <span
                      aria-hidden="true"
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-line-strong bg-surface-raised text-xs font-bold"
                    >
                      {PROVIDER_SIGLA[p.provider] ?? p.provider.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 text-xs leading-tight">
                      <b className="block truncate capitalize">{p.provider.replace(/_/g, ' ')}</b>
                      {/* Estado com TEXTO, nunca so a bolinha colorida. */}
                      <span className={tudoOk ? 'text-ok' : 'text-warn'}>
                        {p.disponiveis} de {p.total} disponíveis
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </section>

      {/* ------------------- Funil ponta-a-ponta (§7.3) ------------------ */}
      <section aria-labelledby="secao-funil">
        <Card>
          <TituloDePainel id="secao-funil" texto="Funil ponta-a-ponta" className="mb-3" />
          <ol className="flex flex-wrap gap-2 text-xs">
            {FUNNEL_STAGES.map((stage) => (
              <li
                key={stage}
                className="rounded-full border border-dashed border-line-strong px-2.5 py-1 text-ink-muted"
              >
                {stage}: <span className="italic text-ink-faint">indisponível</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-ink-faint">
            Etapa sem dado permanece nula e visivelmente incompleta, conforme a especificação.
            Nenhuma etapa é preenchida com zero.
          </p>
        </Card>
      </section>

      {/* ------------- Faixa de tres paineis, como no desenho ------------ */}
      <div className="grid gap-4 lg:grid-cols-3">
        <section aria-labelledby="secao-efeito">
          <Card className="h-full">
            <TituloDePainel
              id="secao-efeito"
              texto="Pode causar efeito externo?"
              className="mb-3"
            />
            <ul className="space-y-1.5 text-sm">
              {Object.entries(switches).map(([name, on]) => (
                <li key={name} className="flex items-center justify-between gap-2">
                  <span className="text-ink-muted">{EFFECT_LABEL[name] ?? name}</span>
                  {/* Texto, nao so cor: "DESLIGADO" e legivel em monocromatico. */}
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                      on
                        ? 'border-warn-border bg-warn-surface text-warn-ink'
                        : 'border-line bg-surface-sunken text-ink-muted'
                    }`}
                  >
                    {on ? 'LIGADO' : 'DESLIGADO'}
                  </span>
                </li>
              ))}
            </ul>
            {ligados.length === 0 && (
              <p className="mt-3 text-xs text-ink-faint">
                Todos desligados. É o estado esperado antes de credencial, política aprovada e teste
                de release ponta-a-ponta.
              </p>
            )}
          </Card>
        </section>

        <section aria-labelledby="secao-publicacao">
          <Card className="h-full">
            <CabecalhoDePainel
              id="secao-publicacao"
              texto="Publicação"
              para="/controles"
              acao="Configurar"
            />
            <ul className="space-y-1.5 text-sm">
              {PUBLICACAO_LINHAS.map(({ chave, rotulo }) => {
                const p = porChave.get(chave);
                const semValor = p === undefined || p.status !== 'active';
                return (
                  <li key={chave} className="flex items-center justify-between gap-2">
                    <span className="text-ink-muted">{rotulo}</span>
                    <span className="shrink-0" data-testid={`pub-${chave}`}>
                      {semValor ? (
                        <span className="text-xs italic text-ink-faint">não configurado</span>
                      ) : (
                        <span className="font-semibold">{String(p.value)}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-ink-faint">
              Sem versão aprovada pelo dono, o gate correspondente falha fechado. Nenhum valor é
              preenchido com o número do rascunho.
            </p>
          </Card>
        </section>

        <section aria-labelledby="secao-saude">
          <Card className="h-full">
            <CabecalhoDePainel
              id="secao-saude"
              texto="Pausas e projeções"
              para="/master-switch"
              acao="Master Switch"
            />
            <h3 className="text-xs font-semibold text-ink-faint">Pausas ativas</h3>
            {operations.active_pauses.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">Nenhuma pausa ativa.</p>
            ) : (
              <ul className="mt-1 space-y-1 text-sm">
                {operations.active_pauses.map((p) => (
                  <li key={`${p.scope}:${p.scope_id ?? ''}`}>
                    <span className="font-semibold">{p.scope}</span>
                    {p.scope_id ? ` (${p.scope_id})` : ''}:{' '}
                    <span className="text-ink-muted">{p.reason}</span>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-3 text-xs font-semibold text-ink-faint">Projeções de leitura</h3>
            {operations.projections_state === 'not_configured' ||
            operations.projections.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">
                Nenhuma projeção registrada. Não há painel com valor calculado nesta fase.
              </p>
            ) : (
              <ul className="mt-1 space-y-1 text-sm">
                {operations.projections.map((p) => (
                  <li key={p.name} className="flex items-center justify-between gap-2">
                    <span className="text-ink-muted">{p.name}</span>
                    <span className="shrink-0 text-xs text-ink-faint">{p.freshness}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>
      </div>

      <p className="text-xs text-ink-faint">
        Nenhum número desta tela é ilustrativo. Onde não há valor confiável, aparece o estado do
        indicador e o que falta para ele existir.
      </p>
    </div>
  );
}
